// src/components/AdvancedSearch.tsx
import "./AdvancedSearch.scss";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaMapMarkerAlt, FaImage } from 'react-icons/fa';
import Select, { components } from 'react-select';
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Box } from "../../components/styled-system/Box";
import Pagination from "react-bootstrap/Pagination";
import { Spinner, Collapse, Form, Button } from "react-bootstrap";
import Table from "react-bootstrap/Table";
import { SortOrder } from "../../components/table/table";
import { TableHeader } from "../../components/table/TableHeader";
import { AppSeo } from '../../components/app-seo/AppSeo';
import {
  getAdvancedVineyards,
  getVarieties,
  getCountries,
  getRegions,
  getSubRegions,
  getSubSubRegions,
  getLocationVarieties,
  getAdvancedVineyardsCount
} from "../../api/old-vine-registry-api";
import { Vineyard, VineyardsResultPage } from "../../api/models";
import { Paths } from "../../paths";
import { useBreakpoint } from "../../hooks/useBreakpoint";

type Option = { label: string, value: string };

const pageSize = 500;

const Loading = ({ loading }: { loading: boolean }) => (
  <>{loading && <Box display="flex" justifyContent="center" alignItems="center" height="25px" mt="20px">
    <Spinner animation="border" role="status" />
  </Box>}</>
);

const TD = ({ children }: { children: React.ReactNode }) => (
  <td>
    <Box height="100%" display="flex" alignItems="center">
      {children}
    </Box>
  </td>
);

const customSelectStyles = {
  control: (provided) => ({
    ...provided,
    minHeight: '48px',
    height: '48px',
  }),
  valueContainer: (provided) => ({
    ...provided,
    height: '48px',
    padding: '0 6px',
    color: '#333'
  }),
  input: (provided) => ({
    ...provided,
    margin: '0px',
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: '48px',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#000000', // 🔥 Black placeholder text
  }),
};

const CheckboxOption = props => (
  <components.Option {...props}>
    <input
      type="checkbox"
      checked={props.isSelected}
      onChange={() => null}
      style={{ marginRight: 8 }}
    />
    {props.label}
  </components.Option>
);

// 1) define your options array
const ageOptions = [
  { value: "35-54",  label: "35-54 years" },
  { value: "55-74",  label: "55-74 years" },
  { value: "75-94",  label: "75-94 years" },
  { value: "95-114", label: "95-114 years" },
  { value: "115-134",label: "115-134 years" },
  { value: "135-154",label: "135-154 years" },
  { value: "155+",   label: "More than 155 years" },
];

// 1) define your options array
const yesNoOptions = [
  { value: "yes",  label: "yes" },
  { value: "no",  label: "no" },
];

function useVineyardsLoader(queryOn: {
  search?: boolean;
  country?: string;
  region?: string;
  subRegion?: string;
  subSubRegion?: string;
  hasCoordinates?: boolean;
  hasPictures?: boolean;
  producer?: string;
  varieties?: string[];
  ageRange?: string[];
  isOwnRooted?: string[];
  elevationRange?: string;
  sizeRange?: string;
}) {
  const [vineyards, setVineyards] = useState<Vineyard[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  
  const currentParams = () => {
    const p: Record<string, string> = {};
    ["search","country", "region","subRegion","subSubRegion","producer","hasCoordinates","hasPictures","varieties","ageRanges","isOwnRooted","sizeRange","elevationRange"].forEach((key) => {
      const v = searchParams.get(key);
      if (v) p[key] = v;
    });
    return p;
  };

  const loadData = async (
    filters: typeof queryOn,
    orderBy = "default",
    order: SortOrder = "asc",
    page = 0
  ) => {

    setLoading(true);
    let pageData: VineyardsResultPage;

    const params = {
      search: filters.search || false,
      country: filters.country || "",
      region: filters.region || "",
      subRegion: filters.subRegion || "",
      subSubRegion: filters.subSubRegion || "",
      elevationRange: filters.elevationRange || "",
      sizeRange: filters.sizeRange || "",
      hasCoordinates: filters.hasCoordinates || false,
      hasPictures: filters.hasPictures || false,
      producer: filters.producer || "",
      varieties: filters.varieties || [],
      ageRange: filters.ageRange || [],
      isOwnRooted: filters.isOwnRooted || [],
      field: orderBy,
      order: order,
      pageNumber: page,
      pageSize: pageSize
    }
    
    const searchEnabled = params.search

    if (!searchEnabled) { 
        console.log("get vineyards: ", params)
        pageData = await getAdvancedVineyards({ ...params,  });
        setLoading(false);
        setVineyards(pageData.content || []);
        setTotalResults(pageData.totalElements);
        setTotalPages(pageData.totalPages);
        setCurrentPage(page);
    }
    else {
      console.log("get count: ", params)
      setTotalResults(await getAdvancedVineyardsCount({ ...params,  }));
    }
    
  };

  const firstPage = (field = "default", order: SortOrder = "asc") => {
    setCurrentPage(0);
    setSearchParams({ ...currentParams(), page: "1", sort: field, order });
  };

  const prevPage = (f = "default", o: SortOrder = "asc") => {
    if (currentPage > 0) {
      const p = currentPage - 1;
      setCurrentPage(p);
      setSearchParams({ ...currentParams(), page: `${p + 1}` });
    }
  };
  const nextPage = (f = "default", o: SortOrder = "asc") => {
    if (currentPage < totalPages - 1) {
      const p = currentPage + 1;
      setCurrentPage(p);
      setSearchParams({ ...currentParams(), page: `${p + 1}` });
    }
  };
  const lastPage = (f = "default", o: SortOrder = "asc") => {
    const p = totalPages - 1;
    setCurrentPage(p);
    setSearchParams({ ...currentParams(), page: `${p + 1}` });
  };

  return {
    loading,
    data: vineyards,
    totalResults,
    totalPages,
    currentPage,
    loadData,
    firstPage,
    prevPage,
    nextPage,
    lastPage,
  };
}

export const AdvancedSearch = () => {
  // Save scroll position
  const filterControlsRef = useRef<HTMLDivElement | null>(null);
  const varietyFilterRef = useRef<HTMLDivElement | null>(null);
  const ageFilterRef = useRef<HTMLDivElement | null>(null);

  function updateFilterPreservingScroll(updateFn: () => void) {
    const scrollY = window.scrollY;
    updateFn();
    setTimeout(() => {
      window.scrollTo({ top: scrollY });
    }, 100);
  }

  useEffect(() => {
    console.log("Page mounted");
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const bp = useBreakpoint();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Sorting state
  const [sortByField, setSortByField] = useState<string>(
    () => searchParams.get("sort") || "default"
  );
  const [sortOrder, setSortOrder] = useState<SortOrder>(
    () => (searchParams.get("order") as SortOrder) || "asc"
  );

  // Search & filters
  const [countries, setCountries] = useState<string[]>([]);
  const [regions, setRegions] = useState<string[]>([]);
  const [subRegions, setSubRegions] = useState<string[]>([]);
  const [subSubRegions, setSubSubRegions] = useState<string[]>([]);
  const [varieties, setVarieties] = useState<string[]>([]);

  const [filtersOpen, setFiltersOpen] = useState(true);

  const [searchFilter, setSearchFilter] = useState<string>(
    () => searchParams.get("search") || ""
  );

  const [countryFilter, setCountryFilter] = useState<string>(
    () => searchParams.get("country") || ""
  );
  
  const [regionFilter, setRegionFilter] = useState<string>(
    () => searchParams.get("region") || ""
  );
  
  const [subRegionFilter, setSubRegionFilter] = useState<string>(
    () => searchParams.get("subRegion") || ""
  );
  
  const [subSubRegionFilter, setSubSubRegionFilter] = useState<string>(
    () => searchParams.get("subSubRegion") || ""
  );
  
  const [coordinateFilter, setCoordinateFilter] = useState<string>(
    () => searchParams.get("hasCoordinates") || ""
  );
  
  const [pictureFilter, setPictureFilter] = useState<string>(
    () => searchParams.get("hasPictures") || ""
  );
  
  const [producerFilter, setProducerFilter] = useState<string>(
    () => searchParams.get("producer") || ""
  );

  const [sizeRangeFilter, setSizeRangeFilter] = useState<{ min: string, max: string }>(() => {
    const range = searchParams.get("sizeRange") || "";
    const [min = "", max = ""] = range.split("-");
    return { min, max };
  });

  const [elevationRangeFilter, setElevationRangeFilter] = useState<{ min: string, max: string }>(() => {
    const range = searchParams.get("elevationRange") || "";
    const [min = "", max = ""] = range.split("-");
    return { min, max };
  });

  const [varietyFilter, setVarietyFilter] = useState<string[]>(
                    () => searchParams.getAll("variety") || []);
  const [ageRangeFilter, setAgeRangeFilter] = useState<string[]>(
                    () => searchParams.getAll("ageRange") || []);
  const [isOwnRootedFilter, setIsOwnRootedFilter] = useState<string[]>(
                    () => searchParams.getAll("isOwnRooted") || []);

  const varietyOptions: Option[] = varieties.map(v => ({ label: v, value: v }));
  const selectedVarietyOptions = varietyFilter.map(v => ({ label: v, value: v }));
  const selectedAgeRangeOptions = ageRangeFilter.map(v => ({ label: v, value: v }));
  const selectedIsOwnRootedOptions = isOwnRootedFilter.map(v => ({ label: v, value: v }));

  const clearAllFilters = () => {
    setSearchFilter("false");
    setCountryFilter('');
    setRegionFilter('');
    setSubRegionFilter('');
    setSubSubRegionFilter('');
    setCoordinateFilter('');
    setPictureFilter('');
    setProducerFilter('');
    setSizeRangeFilter({min: '', max: ''});
    setElevationRangeFilter({min: '', max: ''});
    setVarietyFilter([]);
    setAgeRangeFilter([]);
    setIsOwnRootedFilter([]);
    setSortByField('default');
    setSortOrder('asc');

    // Clear query params
    setSearchParams({});

    // Reset page and reload data
    firstPage("default", "asc");
  };

  // Count non-empty params only
  const hasAnyParams = Array.from(searchParams.entries())
    .filter(([_, value]) => value !== '')
    .length > 0;

  const queryOn = useMemo(() => ({
    search: searchFilter === "false",
    country: countryFilter,
    region: regionFilter,
    subRegion: subRegionFilter,
    subSubRegion: subSubRegionFilter,
    producer: producerFilter,
    sizeRange: `${sizeRangeFilter.min === "" ? 0 : sizeRangeFilter.min}-${sizeRangeFilter.max === "" ? 10000 : sizeRangeFilter.max}`,
    elevationRange: `${elevationRangeFilter.min === "" ? 0 : elevationRangeFilter.min}-${elevationRangeFilter.max === "" ? 10000 : elevationRangeFilter.max}`,
    hasCoordinates: coordinateFilter === "true",
    hasPictures: pictureFilter === "true",
    varieties: varietyFilter,
    ageRange: ageRangeFilter,
    isOwnRooted: isOwnRootedFilter,
  }), [
    searchFilter,
    countryFilter,
    regionFilter,
    subRegionFilter,
    subSubRegionFilter,
    producerFilter,
    sizeRangeFilter,
    elevationRangeFilter,
    coordinateFilter,
    pictureFilter,
    varietyFilter,
    ageRangeFilter,
    isOwnRootedFilter
  ]);

  // Load vineyard data
  const {
    loading,
    data: tableData,
    totalResults,
    totalPages,
    currentPage,
    firstPage,
    prevPage,
    nextPage,
    lastPage,
    loadData,
  } = useVineyardsLoader(queryOn);

  useEffect(() => {
    if (hasAnyParams) 
    {
      loadData({ ...queryOn}, sortByField, sortOrder, currentPage);
    }
    if (searchFilter === "true") {
      setFiltersOpen(false); 
    }
    else {
      setFiltersOpen(true);
    }
      
  }, [location.key,
      searchFilter,
      countryFilter,
      regionFilter,
      subRegionFilter,
      subSubRegionFilter,
      coordinateFilter,
      pictureFilter,
      producerFilter,
      sizeRangeFilter,
      elevationRangeFilter,
      varietyFilter.length,
      ageRangeFilter.length,
      isOwnRootedFilter.length,
      sortByField,
      sortOrder,
      hasAnyParams,
      currentPage,
      queryOn
      ]);

  // 1) Fetch countries on mount
  useEffect(() => {
    getCountries().then(setCountries).catch(console.error);
  }, []);

  // 2) Whenever countryFilter changes, reload regions
  useEffect(() => {
    if (countryFilter) {
      getRegions(countryFilter)
        .then((list) => {
          setRegions(list);
          // clear region if no longer valid
          if (!list.includes(regionFilter)) {
            setRegionFilter("");
          }
        })
        .catch(console.error);
        
      if (!regionFilter) {
        getLocationVarieties(countryFilter, "Country")
        .then((list) => {
          setVarieties(list);
        })
        .catch(console.error);
      }
    } else {
      setRegions([]);
      setRegionFilter("");
      getVarieties()
        .then(setVarieties)
        .catch(console.error);
    }
  }, [countryFilter, regionFilter]);

  // 2) Whenever regionFilter changes, reload regions
  useEffect(() => {
    if (regionFilter) {
      getSubRegions(regionFilter)
        .then((list) => {
          setSubRegions(list);
          // clear region if no longer valid
          if (!list.includes(subRegionFilter)) {
            setSubRegionFilter("");
          }
        })
        .catch(console.error);
      if (!subRegionFilter) {
        getLocationVarieties(regionFilter, "Region")
        .then((list) => {
          console.log("Varieties for region:", list);
          setVarieties(list);
        })
        .catch(console.error);
      }
    } else {
      setSubRegions([]);
      setSubRegionFilter("");
    }
  }, [regionFilter, subRegionFilter]);

  // 2) Whenever regionFilter changes, reload regions
  useEffect(() => {
    if (subRegionFilter) {
      getSubSubRegions(subRegionFilter)
        .then((list) => {
          setSubSubRegions(list);
          // clear region if no longer valid
          if (!list.includes(subSubRegionFilter)) {
            setSubSubRegionFilter("");
          }
        })
        .catch(console.error);
      getLocationVarieties(subRegionFilter, "SubRegion")
        .then((list) => {
          console.log("Varieties for subregion:", list);
          setVarieties(list);
        })
        .catch(console.error);
    } else {
      setSubSubRegions([]);
      setSubSubRegionFilter("");
    }
  }, [subRegionFilter, subSubRegionFilter]);

  useEffect(() => {
    getVarieties()
      .then(setVarieties)
      .catch(console.error);
}, []);

  // Sync URL params when filters/sort/page change
  useEffect(() => {
    const p = new URLSearchParams();
    if (searchFilter)       p.set("search",searchFilter);
    if (countryFilter)      p.set("country",countryFilter);
    if (regionFilter)       p.set("region",regionFilter);
    if (subRegionFilter)    p.set("subRegion",subRegionFilter);
    if (subSubRegionFilter) p.set("subSubRegion",subSubRegionFilter);
    if (coordinateFilter)   p.set("hasCoordinates", coordinateFilter);
    if (pictureFilter)      p.set("hasPictures", pictureFilter);
    if (producerFilter)     p.set("producer", producerFilter);
    if (sizeRangeFilter.min || sizeRangeFilter.max) {
      p.set("sizeRange", `${sizeRangeFilter.min}-${sizeRangeFilter.max}`);
    }
    if (elevationRangeFilter.min || elevationRangeFilter.max) {
      p.set("elevationRange", `${elevationRangeFilter.min}-${elevationRangeFilter.max}`);
    }
    if (varietyFilter)      varietyFilter.forEach(v => p.append("variety", v));
    if (ageRangeFilter)     ageRangeFilter.forEach(v => p.append("ageRange", v));
    if (isOwnRootedFilter)  isOwnRootedFilter.forEach(v => p.append("isOwnRooted", v));
    if (sortByField)        p.set("sort",sortByField);
    if (sortOrder)          p.set("order",sortOrder);
    if (sortOrder)          p.set("page",`${currentPage + 1}`);
    setSearchParams(p);
  }, [searchFilter, countryFilter, regionFilter, subRegionFilter, subSubRegionFilter, 
      coordinateFilter, pictureFilter, producerFilter,
      sizeRangeFilter, elevationRangeFilter,
      varietyFilter, ageRangeFilter, isOwnRootedFilter,
      sortByField, sortOrder, currentPage, setSearchParams]);

  // Document title
  useEffect(() => {
    document.title = "Old Vine Registry Results";
    return () => {
      document.title = "Old Vine Registry";
    };
  }, []);

  // Handlers
  const reloadAfterSort = (field: string, order: SortOrder) => {
    const newOrder = field === sortByField ? order : "asc";
    setSortByField(field);
    setSortOrder(newOrder);
    firstPage(field, newOrder);
  };

  const runTextQuery = () => {
    setSearchFilter("true");
  };

  const disableSearch = !countryFilter && !regionFilter && !subRegionFilter && !subSubRegionFilter
                         && !((coordinateFilter ?? "") === "true") && !((pictureFilter ?? "") === "true")
                         && !producerFilter && !sizeRangeFilter && !elevationRangeFilter
                         && varietyFilter.length === 0 && ageRangeFilter.length === 0 && isOwnRootedFilter.length === 0

  return (
    <><AppSeo
        title="Advanced Search"
        keywords="old vines, vineyards, wine heritage, vintage wines, advanced search"
      />
    <div className="content-panel">
      {!filtersOpen && 
          <p className="header-panel">
            <span className="title-large">Your query returned {totalResults} results.</span>
          </p>
        }
      {filtersOpen && 
        <p className="header-panel">
          <span className="title-large">Advanced Search</span>
        </p>
      } 
      
      {/* Active filters as pills */}
      <Box className="active-filters" mt="16px">
        <div>
          <span className="form-label">
            <b>Active Filters</b>:
          </span>
        </div>
        <div className="active-filter-pills">
          {countryFilter && (
            <div className="form-label filter-button">
              Country: {countryFilter}{' '}
              <Button
                variant="close"
                size="sm"
                aria-label="Clear country filter"
                onClick={() => {
                  setCountryFilter('');
                  firstPage(sortByField, sortOrder);
                }}
              />
            </div>
          )}

          {regionFilter && (
            <div className="form-label filter-button" >
              Region: {regionFilter}{' '}
              <Button
                variant="close"
                size="sm"
                aria-label="Clear region filter"
                onClick={() => {
                  setRegionFilter('');
                  firstPage(sortByField, sortOrder);
                }}
              />
            </div>
          )}

          {subRegionFilter && (
            <div className="form-label filter-button" >
              Subregion: {subRegionFilter}{' '}
              <Button
                variant="close"
                size="sm"
                aria-label="Clear subregion filter"
                onClick={() => {
                  setSubRegionFilter('');
                  firstPage(sortByField, sortOrder);
                }}
              />
            </div>
          )}

          {subSubRegionFilter && (
            <div className="form-label filter-button" >
              Sub-subregion: {subSubRegionFilter}{' '}
              <Button
                variant="close"
                size="sm"
                aria-label="Clear sub-subregion filter"
                onClick={() => {
                  setSubSubRegionFilter('');
                  firstPage(sortByField, sortOrder);
                }}
              />
            </div>
          )}

          {varietyFilter.length > 0 && (() => {
            const fullLabel = varietyFilter.join(", ");
            const displayLabel =
              fullLabel.length > 20
                ? fullLabel.substring(0, 20) + "…"
                : fullLabel;
            return (
              <div className="form-label filter-button">
                Grape Variety: {displayLabel}{" "}
                <Button
                  variant="close"
                  size="sm"
                  aria-label="Clear variety filter"
                  onClick={() => {
                    setVarietyFilter([]);
                    firstPage(sortByField, sortOrder);
                  }}
                />
              </div>
            );
          })()}

          {ageRangeFilter.length > 0 && (() => {
            const fullLabel = ageRangeFilter.join(", ");
            const displayLabel =
              fullLabel.length > 20
                ? fullLabel.substring(0, 20) + "…"
                : fullLabel;
            return (
              <div className="form-label filter-button">
                Age of Vines: {displayLabel}{" "}
                <Button
                  variant="close"
                  size="sm"
                  aria-label="Clear age range filter"
                  onClick={() => {
                    setAgeRangeFilter([]);
                    firstPage(sortByField, sortOrder);
                  }}
                />
              </div>
            );
          })()}

          {isOwnRootedFilter.length > 0 && (() => {
            const fullLabel = isOwnRootedFilter.join(", ");
            const displayLabel =
              fullLabel.length > 20
                ? fullLabel.substring(0, 20) + "…"
                : fullLabel;
            return (
              <div className="form-label filter-button">
                Own Rooted: {displayLabel}{" "}
                <Button
                  variant="close"
                  size="sm"
                  aria-label="Clear is own rooted filter"
                  onClick={() => {
                    setIsOwnRootedFilter([]);
                    firstPage(sortByField, sortOrder);
                  }}
                />
              </div>
            );
          })()}

          {producerFilter && (
            <div className="form-label filter-button">
              Producer Name: {producerFilter}{' '}
              <Button
                variant="close"
                size="sm"
                aria-label="Clear producer filter"
                onClick={() => {
                  setProducerFilter('');
                  firstPage(sortByField, sortOrder);
                }}
              />
            </div>
          )}

          {elevationRangeFilter && elevationRangeFilter.min !== "" && elevationRangeFilter.max !== "" && (
            <div className="form-label filter-button">
              Elevation: {`${elevationRangeFilter.min}-${elevationRangeFilter.max}`}{' '}
              <Button
                variant="close"
                size="sm"
                aria-label="Clear producer filter"
                onClick={() => {
                  setElevationRangeFilter({min: '', max: ''});
                  firstPage(sortByField, sortOrder);
                }}
              />
            </div>
          )}
          
          {sizeRangeFilter && !(sizeRangeFilter.min === "" && sizeRangeFilter.max === "") && (
            <div className="form-label filter-button">
              Size Range: {`${sizeRangeFilter.min === "" ? "" : " > " + sizeRangeFilter.min} ${sizeRangeFilter.min !== "" && sizeRangeFilter.max !== "" ? " & " : " "} ${sizeRangeFilter.max === "" ? "" : "< " + sizeRangeFilter.max}`}{' '}
              <Button
                variant="close"
                size="sm"
                aria-label="Clear producer filter"
                onClick={() => {
                  setSizeRangeFilter({min: '', max: ''});
                  firstPage(sortByField, sortOrder);
                }}
              />
            </div>
          )}



          {coordinateFilter && coordinateFilter === "true" && (
            <div className="form-label filter-button" >
              Exact Coordinates: {coordinateFilter === "true" ? "yes" : ""}{' '}
              <Button
                variant="close"
                size="sm"
                aria-label="Clear region filter"
                onClick={() => {
                  setCoordinateFilter('');
                  firstPage(sortByField, sortOrder);
                }}
              />
            </div>
          )}

          {pictureFilter && pictureFilter === "true" && (
            <div className="form-label filter-button" >
              Has Photos: {pictureFilter === "true" ? "yes" : ""}{' '}
              <Button
                variant="close"
                size="sm"
                aria-label="Clear region filter"
                onClick={() => {
                  setPictureFilter('');
                  firstPage(sortByField, sortOrder);
                }}
              />
            </div>
          )}
          </div>
          <div>
          
          </div>
        </Box>

        {!filtersOpen && 
          <p className="header-panel">
            <a
              className="menu-heading"
              href={`${Paths.advancedSearch}`}
              target="_blank" // Open in a new tab
              rel="noopener noreferrer" // Added for security
              onClick={(e) => {
                setSearchFilter("false");
                e.preventDefault(); // Prevent the default link behavior
                navigate(`${Paths.advancedSearch}`);
              }}>
              <span className="search-link">Back to Advanced Search</span>
            </a> 
          </p>
        }

        <Collapse timeout={0} in={filtersOpen}>
          <Box className="filter-controls" ref={filterControlsRef}>
          <div>
            <p><span className="form-label">
                Search the Old Vine Registry database using multiple criteria. Select any and all criteria important to your query.
              </span>
            </p>
            <p>
              <span className="sub-headline">
                Location
              </span>
            </p>

            {/* Filters: Country, Region, Sub Region */}
            <div className="description-text select-container">
              <Box>
                <span className="select-label u-mr-small">
                  Country
                </span>
                <Box display="flex" alignItems="center">
                  <Box className="search-combo" display="inline-block">
                    <Form.Select
                      className="input-text input-height"
                      value = {countryFilter}
                      onChange={(e) => setCountryFilter(e.target.value)}
                    >
                      <option value="">Select country</option>
                        {countries.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))} 
                    </Form.Select>
                  </Box>
                </Box>
              </Box>
              <Box>
                <span className="select-label u-mr-small">
                  Region
                </span>
                <Box display="flex" alignItems="center">
                  <Box className="search-combo" display="inline-block">
                    <Form.Select
                      className="input-text input-height"
                      value = {regionFilter}
                      disabled={!regions.length}
                      onChange={(e) => {setRegionFilter(e.target.value);}
                                }>
                      <option value="">Select region</option>
                        {regions.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                    </Form.Select>
                  </Box>
                </Box>
              </Box>
              <Box>
                <span className="select-label u-mr-small">
                  Subregion
                </span>
                <Box display="flex" alignItems="center">
                  <Box className="search-combo" display="inline-block">
                    <Form.Select
                      className="input-text input-height"
                      value = {subRegionFilter}
                      disabled={!subRegions.length}
                      onChange={(e) => {setSubRegionFilter(e.target.value);}
                                }>
                      <option value="">Select subregion</option>
                        {subRegions.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                    </Form.Select>
                  </Box>
                </Box>
              </Box>
              <Box>
                <span className="select-label u-mr-small">
                  Sub-Subregion
                </span>
                <Box display="flex" alignItems="center">
                  <Box className="search-combo" display="inline-block">
                    <Form.Select
                      className="input-text input-height"
                      value = {subSubRegionFilter}
                      disabled={!subSubRegions.length}
                      onChange={(e) => {setSubSubRegionFilter(e.target.value);}
                                }>
                      <option value="">Select sub-subregion</option>
                        {subSubRegions.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                    </Form.Select>
                  </Box>
                </Box>
              </Box>
            </div>
            
            
              <p>
                <span className="sub-headline">
                  Characteristics
                </span>
              </p>
              {/* Variety multi-select */}
              <div className="description-text select-container">
                <Box ref={varietyFilterRef}>
                  <span className="select-label u-mr-small">
                    Grape Varieties
                  </span>
                  <Select
                    isMulti
                    placeholder="Select grape varieties"
                    className="input-text"
                    styles={customSelectStyles}
                    options={varietyOptions}         // `{ label: v, value: v }[]`
                    value={selectedVarietyOptions}          // same shape
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    components={{ Option: CheckboxOption }}
                    onChange={opts => {
                      updateFilterPreservingScroll(() => {
                        setVarietyFilter(opts.map(o => o.value));
                        // Only scroll if on desktop or if explicitly needed
                        setTimeout(() => {
                          varietyFilterRef.current?.scrollIntoView({ behavior: 'auto', block: 'start' });
                        }, 100);
                      });
                    }}/>
                </Box>
                <Box ref={ageFilterRef}>
                  <span className="select-label u-mr-small">
                    Age of Vines
                  </span>
                  <Select
                    isMulti
                    placeholder="Select age ranges"
                    className="input-text"
                    styles={customSelectStyles}
                    options={ageOptions}         // `{ label: v, value: v }[]`
                    value={selectedAgeRangeOptions}          // same shape
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    components={{ Option: CheckboxOption }}
                    onChange={(opts) => {
                                
                                setAgeRangeFilter(opts.map(o => o.value));
                                setTimeout(() => {
                                  ageFilterRef.current?.scrollIntoView({ behavior: 'auto', block: 'start' });
                                }, 100);
                                           
                                }
                                }>
                  </Select>
                </Box>
                <Box>
                  <span className="select-label u-mr-small">
                    Own Rooted?
                  </span>
                  <Select
                    isMulti
                    placeholder="All"
                    className="input-text"
                    styles={customSelectStyles}
                    options={yesNoOptions}         // `{ label: v, value: v }[]`
                    value={selectedIsOwnRootedOptions}          // same shape
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    components={{ Option: CheckboxOption }}
                    onChange={opts => setIsOwnRootedFilter(opts.map(o => o.value))}>
                  </Select>
                </Box>
                <Box>
                    <span className="select-label u-mr-small">
                      Producer Name
                    </span>
                    <Form.Control
                      placeholder="Enter Producer Name"
                      className="input-text input-search"
                      onChange={(e) => setProducerFilter(e.target.value)}
                    />
                  </Box>
              </div>
              <div className="description-text select-container">
                <Box>
                    <span className="select-label u-mr-small">
                      Elevation (meters)
                    </span>
                    <div className="size-range">
                      <Form.Control
                        type="number"
                        step="1"
                        placeholder="Min"
                        className="input-text input-search"
                        value={elevationRangeFilter.min}
                        onChange={(e) =>
                          setElevationRangeFilter((prev) => ({ ...prev, min: e.target.value }))
                        }
                      />
                      <div>to</div>
                      <Form.Control
                        type="number"
                        step="1"
                        placeholder="Max"
                        className="input-text input-search"
                        value={elevationRangeFilter.max}
                        onChange={(e) =>
                          setElevationRangeFilter((prev) => ({ ...prev, max: e.target.value }))
                        }
                      />
                    </div>
                  </Box>
                  <Box>
                    <span className="select-label u-mr-small">
                      Size of Planting (hectares)
                    </span>
                    <div className="size-range">
                      <Form.Control
                        type="number"
                        step="1"
                        placeholder="Min"
                        className="input-text input-search"
                        value={sizeRangeFilter.min}
                        onChange={(e) =>
                          setSizeRangeFilter((prev) => ({ ...prev, min: e.target.value }))
                        }
                      />
                      <div>to</div>
                      <Form.Control
                        type="number"
                        step="1"
                        placeholder="Max"
                        className="input-text input-search"
                        value={sizeRangeFilter.max}
                        onChange={(e) =>
                          setSizeRangeFilter((prev) => ({ ...prev, max: e.target.value }))
                        }
                      />
                    </div>
                  </Box>
              </div>

              {/* Variety multi-select */}
              <div className="description-text select-container">
                  <Box className="filter-check">
                    <Form.Check id="coordinate-filter-checkbox">
                      <Form.Check.Input
                        type="checkbox"
                        className="checkbox"
                        checked={coordinateFilter === "true"}
                        onChange={(e) => {setCoordinateFilter(e.target.checked ? "true" : "false");
                                          }
                         }
                        isValid={false}
                        isInvalid={false}
                      />
                    </Form.Check>
                    <Form.Check.Label className="input-text check-input">
                      <div className="checkbox-text">
                          <div><FaMapMarkerAlt /></div>
                          <div>Restrict results to vineyards with exact coordinates</div> 
                      </div>
                    </Form.Check.Label>
                    
                  </Box>
                  <Box className="filter-check">
                    <Form.Check id="picture-filter-checkbox">
                      <Form.Check.Input
                        type="checkbox"
                        className="checkbox"
                        checked={pictureFilter === "true"}
                        onChange={e => setPictureFilter(e.target.checked ? "true" : "false")}
                        isValid={false}
                        isInvalid={false}
                      />
                    </Form.Check>
                    <Form.Check.Label
                        className="input-text check-input"
                      >
                        <div className="checkbox-text"><div><FaImage /></div>
                              <div>Restrict results to vineyards with photos</div>
                        </div>
                      </Form.Check.Label>
                  </Box>
              </div>
            
            <button
                className="button u-ml-small search-button"
                disabled={disableSearch}
                onClick={clearAllFilters}>
                Clear All
            </button>
            <button
                className="button u-ml-small search-button"
                disabled={disableSearch}
                onClick={runTextQuery}>
                <span className="button-text">Search</span>
            </button>
            { !disableSearch && <span className="search-result">
                <i>Your filter matches {totalResults} results</i>
              </span> 
            }
          </div>
          </Box>
        </Collapse>

        {tableData.length > 0 && !filtersOpen && (
          <>
            {totalPages > 1 && (
              <div className="--flex" style={{ height: "40px", display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                 <div className="--flex" style={{ display: 'flex', justifyContent: 'flex-start'}}>
                    <Loading loading={loading} />
                 </div>
              <div className="--flex" style={{ display: 'flex', justifyContent: 'flex-end'}}>

                <Box display="flex" alignItems="center" height={"25px"}>
                  <span className="description-text">
                    Page {currentPage + 1} of {totalPages}
                  </span>
                  <Pagination className="u-ml-medium">
                    <Pagination.First
                      onClick={() => firstPage(sortByField, sortOrder)}
                      disabled={currentPage === 0}
                    />
                    <Pagination.Prev
                      onClick={() => prevPage(sortByField, sortOrder)}
                      disabled={currentPage === 0}
                    />
                    <Pagination.Next
                      onClick={() => nextPage(sortByField, sortOrder)}
                      disabled={currentPage === totalPages - 1}
                    />
                    <Pagination.Last
                      onClick={() => lastPage(sortByField, sortOrder)}
                      disabled={currentPage === totalPages - 1}
                    />
                  </Pagination>
                </Box>
              </div>
            </div>
          )}
            <Table hover size="sm" className="vineyard-table input-text">
              <thead>
                <tr>
                  <TableHeader
                    field="name"
                    sortOrder={sortOrder}
                    sortByField={sortByField}
                    onOrderChange={(field, nextOrder) => reloadAfterSort(field, nextOrder)}
                  >
                    Vineyard name
                  </TableHeader>
                  <TableHeader
                    field="country"
                    sortOrder={sortOrder}
                    sortByField={sortByField}
                    onOrderChange={(field, nextOrder) => reloadAfterSort(field, nextOrder)}
                  >
                    Country
                  </TableHeader>
                  {bp === "desktop" && (
                    <>
                      <TableHeader
                        field="region"
                        sortOrder={sortOrder}
                        sortByField={sortByField}
                        onOrderChange={(field, nextOrder) => reloadAfterSort(field, nextOrder)}
                      >
                        Region
                      </TableHeader>
                      <TableHeader
                        field="subRegion"
                        sortOrder={sortOrder}
                        sortByField={sortByField}
                        onOrderChange={(field, nextOrder) => reloadAfterSort(field, nextOrder)}
                      >
                        Subregion
                      </TableHeader>
                      <TableHeader
                        field="subSubRegion"
                        sortOrder={sortOrder}
                        sortByField={sortByField}
                        onOrderChange={(field, nextOrder) => reloadAfterSort(field, nextOrder)}
                      >
                        Sub-Subregion
                      </TableHeader>
                      <TableHeader
                        field="yearPlanted"
                        sortOrder={sortOrder}
                        sortByField={sortByField}
                        onOrderChange={(field, nextOrder) => reloadAfterSort(field, nextOrder)}
                        width="100px"
                      >
                        Date planted
                      </TableHeader>
                    </>
                  )}
                  <TableHeader
                    field="ageOfVines"
                    sortOrder={sortOrder}
                    sortByField={sortByField}
                    onOrderChange={(field, nextOrder) => reloadAfterSort(field, nextOrder)}
                    width="50px"
                  >
                    Age
                  </TableHeader>
                  {bp === "desktop" && (
                      <>
                        <th style={{width: "20px"}}/>
                        <th style={{width: "20px"}}/>
                      </>
                  )}
                </tr>
              </thead>
              <tbody>
                {tableData.map((row, i) => (
                  <tr key={i}>
                    <TD>
                      <a
                        href={`${Paths.vineyard.replace(':id', row.composite)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => {
                          if (e.metaKey || e.ctrlKey) return;
                          e.preventDefault();
                          navigate(`${Paths.vineyard.replace(':id', row.composite)}`);
                        }}
                      >
                        <span className="search-link">{row.name}</span>
                      </a>
                    </TD>
                    <TD>{row.country}</TD>
                    {bp === "desktop" && (
                      <>
                        <TD>{row.region}</TD>
                        <TD>{row.subRegion}</TD>
                        <TD>{row.subSubRegion}</TD>
                        <TD>{row.yearPlanted}</TD>
                      </>
                    )}
                    <TD>{row.ageOfVines}</TD>
                    {bp === "desktop" && (
                      <>
                        <TD>{!(row.longitude === "" || row.latitude === "") ? <FaMapMarkerAlt color="#888"/> : <></> }</TD>
                        <TD>{!(row.photoUrl == null || row.photoUrl === "") ? <FaImage color="#888"/> : <></> }</TD>
                      </>
                     )}
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
    </div>
    </>
  );
};
